import { useEffect, useRef, useState } from 'react';
import { http } from '../../services/http';
import { connectSocket, getSocket, onInvalidate } from '../../services/socket';
import type { Notification as AppNotification, UserStatus } from '../../types';
import { humanizeIsoInText } from '../../services/datetime';

export function useNotifications(isAuthenticated: boolean, _userStatus: UserStatus, currentUserId?: string | null) {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const lastCountRef = useRef(0);
  const lastFetchAtRef = useRef(0);
  const notificationIcon = typeof window !== 'undefined' && window.desktopApi ? '/notification.png' : '/favicon.ico';
  const currentUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    currentUserIdRef.current = currentUserId ? String(currentUserId) : null;
  }, [currentUserId]);

  useEffect(() => {
    if (!isAuthenticated) return;

    let stopped = false;
    let timer: any = null;

    const nextDelayMs = () => {
      const visible = typeof document === 'undefined' ? true : document.visibilityState === 'visible';
      const s = getSocket();
      const connected = !!(s && s.connected);
      const base = connected
        ? (visible ? 900000 : 1800000)
        : (visible ? 120000 : 300000);
      const jitter = Math.floor(Math.random() * 2000);
      return base + jitter;
    };

    let inflight = false;
    const fetchNotifications = async (opts?: { force?: boolean }) => {
      if (inflight) return;
      const now = Date.now();
      if (!opts?.force && now - lastFetchAtRef.current < 15_000) return;
      inflight = true;
      try {
        const raw = await http.get<any[]>('/notifications');
        const data = raw.map((n) => ({ ...n, date: new Date(n.date) })) as AppNotification[];

        // Filter out dismissed system notifications stored locally
        let dismissed: Set<string> = new Set();
        try {
          const s = localStorage.getItem('al_dismissed_notifs');
          if (s) dismissed = new Set<string>(JSON.parse(s));
        } catch {}
        const filtered = data.filter((n) => !(n.type === 'system' && dismissed.has(n.id)));

        setNotifications(filtered);

        // Push notifications only when new items appear.
        // The desktop shell must not steal focus automatically; the window opens
        // only after the user clicks the notification itself.
        if (filtered.length > lastCountRef.current) {
          const newItems = filtered.slice(0, filtered.length - lastCountRef.current);

          newItems.forEach((item) => {
            if (typeof window !== 'undefined' && 'Notification' in window) {
              if (document.visibilityState === 'hidden' && Notification.permission === 'granted') {
                const n = new Notification(item.title, {
                  body: humanizeIsoInText(item.text),
                  icon: notificationIcon,
                  tag: item.id,
                });
                try {
                  n.onclick = () => {
                    try { window.desktopApi?.showAndFocus?.(); } catch {}
                    try {
                      if ((item.type === 'chat' || item.type === 'mention') && item.linkId) {
                        window.dispatchEvent(new CustomEvent('al_global_navigate', { detail: { type: 'chat', id: item.linkId, subId: item.subId } }));
                      } else if (item.type === 'calendar' && item.linkId) {
                        window.dispatchEvent(new CustomEvent('al_global_navigate', { detail: { type: 'event', id: item.linkId, subId: item.subId } }));
                      }
                    } catch {}
                  };
                } catch {}
              }
            }
          });
        }
        lastCountRef.current = filtered.length;
        lastFetchAtRef.current = Date.now();
      } catch (e) {
        // no-op
      }
      finally {
        inflight = false;
      }
    };

    const schedule = async () => {
      if (stopped) return;
      const s = getSocket();
      const connected = !!(s && s.connected);
      if (!connected) {
        await fetchNotifications();
      }
      if (stopped) return;
      timer = setTimeout(schedule, nextDelayMs());
    };

    void fetchNotifications({ force: true });
    timer = setTimeout(schedule, nextDelayMs());

    const onVis = () => {
      if (stopped) return;
      if (timer) clearTimeout(timer);
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        void fetchNotifications();
      }
      timer = setTimeout(schedule, nextDelayMs());
    };
    try {
      document.addEventListener('visibilitychange', onVis);
    } catch {}

    const offInvalidate = onInvalidate((e) => {
      if (e.scope === 'notifications') {
        void fetchNotifications({ force: true });
      }
    }, ['notifications']);

    const s = connectSocket();
    const onMsg = (payload?: any) => {
      void fetchNotifications({ force: true });
      try {
        const isDesktop = typeof window !== 'undefined' && !!window.desktopApi;
        if (isDesktop) {
          const chatId = typeof payload?.chatId === 'string' ? payload.chatId : null;
          const senderId = typeof payload?.message?.senderId === 'string' ? String(payload.message.senderId) : null;

          // Only auto-focus/navigate for *incoming* messages.
          // This prevents PC app from switching chats when you act from another device (e.g. Android).
          const myId = currentUserIdRef.current;
          const isOutgoing = !!(myId && senderId && senderId === myId);
          if (chatId && !isOutgoing) {
            try { window.desktopApi?.showAndFocus?.(); } catch {}
            window.dispatchEvent(new CustomEvent('al_global_navigate', { detail: { type: 'chat', id: chatId } }));
          }
        }
      } catch {}
    };
    const onMetaUpdate = () => {
      fetchNotifications();
    };
    try {
      s.on('message_new', onMsg);
      s.on('chat_read', onMetaUpdate);
      s.on('message_delete', onMetaUpdate);
    } catch {}

    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      try {
        document.removeEventListener('visibilitychange', onVis);
      } catch {}
      try { offInvalidate && offInvalidate(); } catch {}
      try {
        s.off('message_new', onMsg);
        s.off('chat_read', onMetaUpdate);
        s.off('message_delete', onMetaUpdate);
      } catch {}
    };
  }, [isAuthenticated, notificationIcon]);

  return notifications;
}
